/**
 * `useAuth` - the only auth hook the rest of the app should reach for.
 *
 * Boots once on mount, resolves the current user (if any) via Amplify,
 * and listens to the Hub for sign-in / sign-out events so social
 * redirects land users in the right state without a manual refresh.
 */
import { Hub } from 'aws-amplify/utils';
import { useEffect } from 'react';

import { identifyUser, resetAnalytics } from '@/lib/analytics';

import { getCurrentUser } from '../api/amplify-auth';
import { useAuthStore } from '../stores/auth-store';

import type { AuthUser } from '../types';

export interface UseAuthReturn {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export function useAuth(): UseAuthReturn {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  return { user, isLoading, isAuthenticated: user !== null };
}

export function useAuthBootstrap(): void {
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      setLoading(true);
      const next = await getCurrentUser();
      if (!cancelled) {
        setUser(next);
        // Tie analytics to the resolved user (or clear it if none).
        if (next) {
          identifyUser(next);
        } else {
          resetAnalytics();
        }
      }
    };

    void resolve();

    const unsubscribe = Hub.listen('auth', (data) => {
      const event = data.payload.event;
      if (event === 'signedIn' || event === 'signInWithRedirect' || event === 'tokenRefresh') {
        void resolve();
      } else if (event === 'signedOut' || event === 'tokenRefresh_failure') {
        setUser(null);
        resetAnalytics();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [setUser, setLoading]);
}
